import { entityToHref } from './entity-utils';
import { notePreview } from './note-utils';

export type DeskSearchResultType =
  | 'project'
  | 'document'
  | 'note'
  | 'character'
  | 'location'
  | 'chapter'
  | 'storyline'
  | 'scene'
  | 'timeline_event';

export interface DeskSearchResult {
  type: DeskSearchResultType;
  id: string;
  label: string;
  projectId: string | null;
  projectTitle: string | null;
  documentId?: string;
  documentTitle?: string;
}

export const DESK_SEARCH_TYPE_ORDER: DeskSearchResultType[] = [
  'project',
  'document',
  'note',
  'character',
  'location',
  'chapter',
  'storyline',
  'scene',
  'timeline_event',
];

export const DESK_SEARCH_TYPE_LABEL_KEYS: Record<DeskSearchResultType, string> = {
  project: 'desk_search_type_project',
  document: 'desk_search_type_document',
  note: 'desk_search_type_note',
  character: 'desk_search_type_character',
  location: 'desk_search_type_location',
  chapter: 'desk_search_type_chapter',
  storyline: 'desk_search_type_storyline',
  scene: 'desk_search_type_scene',
  timeline_event: 'desk_search_type_timeline_event',
};

export function deskSearchResultHref(result: DeskSearchResult): string | undefined {
  if (result.type === 'project') {
    return `/projects/${result.id}`;
  }
  if (result.type === 'note') {
    if (result.projectId) {
      return `/projects/${result.projectId}/notes?noteId=${encodeURIComponent(result.id)}`;
    }
    return '/desk/notes';
  }
  if (!result.projectId) {
    return undefined;
  }
  if (result.type === 'document') {
    return entityToHref(
      { type: 'document', id: result.id, label: result.label },
      result.projectId
    );
  }
  if (result.type === 'timeline_event') {
    return entityToHref(
      { type: 'timeline_item', id: result.id, label: result.label },
      result.projectId
    );
  }
  if (result.type === 'chapter') {
    return entityToHref(
      {
        type: 'manual_chapter',
        id: result.id,
        documentId: result.documentId,
        label: result.label,
      },
      result.projectId
    );
  }
  return entityToHref(
    {
      type: result.type,
      id: result.id,
      documentId: result.documentId,
      label: result.label,
    },
    result.projectId
  );
}

export function groupDeskSearchResults(
  results: DeskSearchResult[]
): Array<{ type: DeskSearchResultType; results: DeskSearchResult[] }> {
  const byType = new Map<DeskSearchResultType, DeskSearchResult[]>();
  for (const result of results) {
    const existing = byType.get(result.type);
    if (existing) {
      existing.push(result);
    } else {
      byType.set(result.type, [result]);
    }
  }
  return DESK_SEARCH_TYPE_ORDER
    .filter((type) => byType.has(type))
    .map((type) => ({
      type,
      results: byType.get(type) ?? [],
    }));
}

export function deskSearchResultSubtitle(result: DeskSearchResult): string | null {
  if (result.type === 'project') {
    return null;
  }
  const parts: string[] = [];
  if (result.projectTitle) {
    parts.push(result.projectTitle);
  }
  if (result.documentTitle && result.type !== 'document' && result.type !== 'note') {
    parts.push(result.documentTitle);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

export function deskSearchResultLabel(result: DeskSearchResult): string {
  if (result.type === 'note') {
    return notePreview(result.label, 80);
  }
  return result.label;
}
